import { useState } from 'react';
import type { getLayerToggleTheme } from '../theme/themeStyles';
import {
  DRONE_ASSET_KINDS,
  DRONE_ASSET_LABEL,
  buildDroneLayerPreviewUrl,
  droneLayerId,
  droneSiteLayerIds,
  findDroneLayer,
  formatDroneFlightDate,
  getDroneSitesByProvince,
  type DroneAssetKind,
  type DroneFlight,
  type DroneSite,
  type DroneSitesControls,
} from '../constants/droneSites';

type LayerToggleTheme = ReturnType<typeof getLayerToggleTheme>;

const ASSET_ICON: Record<DroneAssetKind, string> = {
  ndvi: 'ri-leaf-line',
  ortho: 'ri-image-2-line',
  dem: 'ri-landscape-line',
};

const ASSET_HINT: Record<DroneAssetKind, string> = {
  ndvi: 'Crop vigour',
  ortho: 'True-colour image',
  dem: 'Ground height',
};

/** Matplotlib "terrain" colormap stops — matches the DEM tiles (`colormap_name=terrain`). */
const DEM_LEGEND_GRADIENT =
  'linear-gradient(to right, #333399 0%, #0099ff 15%, #00cc66 25%, #ffff99 50%, #805c54 75%, #ffffff 100%)';

interface DroneSitesSectionProps {
  /** Province whose sites are listed (one header-menu entry per province). */
  province: string;
  controls?: DroneSitesControls;
  isDark: boolean;
  lt: LayerToggleTheme;
  compactSectionPad: string;
  compactLayerPad: string;
}

/** Drone Data → one province: collapsible site cards, each with its flights and NDVI / Ortho / DEM toggles. */
export default function DroneSitesSection({
  province,
  controls,
  isDark,
  lt,
  compactSectionPad,
  compactLayerPad,
}: DroneSitesSectionProps) {
  const sites = getDroneSitesByProvince().find((entry) => entry.province === province)?.sites ?? [];
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (!sites.length) return null;

  if (!controls) {
    return (
      <p className={`text-sm py-3 text-center ${lt.muted}`}>Loading drone layers…</p>
    );
  }

  const activeLayerId = controls.activeLayerId;

  const renderRow = (site: DroneSite, flight: DroneFlight, kind: DroneAssetKind) => {
    const id = droneLayerId(site.id, flight.id, kind);
    const layer = findDroneLayer(id);
    if (!layer) return null;

    const active = activeLayerId === id;
    const opacity = controls.opacity[id] ?? 1;
    const toggle = () => controls.onToggle(id, !active);

    return (
      <div
        key={id}
        role="button"
        tabIndex={0}
        aria-pressed={active}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        onClick={toggle}
        className={`rounded-lg ${compactLayerPad} border transition-all duration-200 cursor-pointer outline-none ${
          active ? lt.layerSelected : lt.layerIdle
        }`}
      >
        <div className="flex gap-2 sm:gap-2.5 items-start pointer-events-none">
          <div
            className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border ${
              isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-200 bg-gray-100'
            }`}
            aria-hidden
          >
            <img
              src={buildDroneLayerPreviewUrl(layer)}
              alt=""
              width={72}
              height={72}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
              <i className={`${ASSET_ICON[kind]} text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`}></i>
              <span
                className={`text-sm font-semibold truncate ${active ? lt.selectedText : lt.heading}`}
              >
                {DRONE_ASSET_LABEL[kind]}
              </span>
            </div>
            {!active && <p className={`text-xs truncate ${lt.muted}`}>{ASSET_HINT[kind]}</p>}
            {active && (
              <div
                className="space-y-0.5 sm:space-y-1 pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${lt.sectionIcon}`}>Opacity</span>
                  <span className={`text-sm font-medium ${lt.selectedText}`}>
                    {Math.round(opacity * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={opacity}
                  onChange={(e) => controls.onOpacityChange(id, parseFloat(e.target.value))}
                  className={`w-full h-1 sm:h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${
                    isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'
                  }`}
                />
                {kind === 'dem' && flight.demRange && (
                  <div className="pt-1">
                    <div className="h-2 rounded" style={{ background: DEM_LEGEND_GRADIENT }} />
                    <div className={`flex justify-between text-xs mt-0.5 ${lt.muted}`}>
                      <span>{flight.demRange[0]} m</span>
                      <span>{flight.demRange[1]} m</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {sites.map((site, siteIndex) => {
        const hasActive = activeLayerId != null && droneSiteLayerIds(site).includes(activeLayerId);
        // First site opens by default so the pinned province view is never a list of bare headers.
        const isOpen = expanded[site.id] ?? (siteIndex === 0 || hasActive);
        const multiFlight = site.flights.length > 1;
        const singleDate = !multiFlight ? formatDroneFlightDate(site.flights[0]?.date) : '';

        return (
          <div key={site.id} className={`mt-2 sm:mt-3 ${lt.section}`}>
            <div
              // Not `layer-section-header`: the pinned (header-menu) view hides that class, which would hide the site names.
              className={`drone-site-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
              onClick={() => setExpanded((prev) => ({ ...prev, [site.id]: !isOpen }))}
            >
              <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
                <i className={`ri-drone-fill text-base sm:text-lg flex-shrink-0 ${lt.sectionIcon}`}></i>
                <h4 className={`text-xs sm:text-sm font-semibold truncate ${lt.heading}`}>
                  {site.label}
                </h4>
                {hasActive && (
                  <span
                    className={`w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0 ${lt.radioDot}`}
                  ></span>
                )}
              </div>
              <i
                className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform flex-shrink-0 ${lt.chevron}`}
              ></i>
            </div>

            {isOpen && (
              <div className="px-2 sm:px-3 pb-2 sm:pb-3 space-y-2">
                <p className={`text-sm mb-1 ${lt.muted}`}>
                  Leads Agri drone survey ({site.province}).
                  {singleDate ? ` Flown ${singleDate}.` : ''}
                  {site.description ? ` ${site.description}` : ''} Tap a layer to view it on the map.
                </p>

                {site.flights.map((flight) => {
                  const kinds = DRONE_ASSET_KINDS.filter((kind) => flight.assets[kind]);
                  if (!kinds.length) return null;
                  const dateLabel = formatDroneFlightDate(flight.date);
                  return (
                    <div key={flight.id} className="space-y-2">
                      {multiFlight && (
                        <p className={`text-xs font-semibold uppercase tracking-wide pt-1 ${lt.sectionIcon}`}>
                          {flight.label}
                          {dateLabel ? ` · ${dateLabel}` : ''}
                        </p>
                      )}
                      {kinds.map((kind) => renderRow(site, flight, kind))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
